import { createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, Outlet, redirect } from '@tanstack/react-router';
import { loadMe } from '@blimo/api-client/react';
import { portalNav } from './nav.js';
import { ForgotPasswordPage, LoginPage, ResetPasswordPage, TwoFactorChallengePage } from './routes/auth-pages.jsx';
import { ProtectedLayout } from './routes/protected-layout.jsx';
import { NotFoundPage, OverviewPage, PlaceholderPage } from './routes/pages.jsx';
import { ProfilePage } from './routes/profile.jsx';
import { TenanciesPage } from './routes/tenancies.jsx';
import { tenancySearch } from './lib/tenancy-search.js';
import { transitionTypes } from './lib/transitions.js';
import { redirectSearch, safeRedirect } from './lib/redirect.js';
import { PropertiesPage, PropertyDetailPage } from './routes/properties.jsx';
import { UnitPage } from './routes/unit.jsx';
import { CostsPage, DevelopmentPage, OpenItemsPage } from './routes/finance.jsx';
import { financeSearch } from '@blimo/shared';
import { DocumentChecksPage } from './routes/document-checks/list.jsx';
import { DocumentCheckPage } from './routes/document-checks/detail.jsx';
import { AnnouncementPage, AnnouncementsPage } from './routes/announcements.jsx';
import { MessagesPage, MessageThreadPage, NewMessagePage } from './routes/messages.jsx';
import { MeterReadingsPage } from './routes/meter-readings.jsx';
import { ProxiesPage, ProxyCreatePage, ProxyPage } from './routes/proxies.jsx';
import { LettingsPage } from './routes/lettings.jsx';
import { ServicePage } from './routes/service.jsx';
import { DocumentsPage } from './routes/documents.jsx';

/** Only positive integer IDs survive (as numbers, so the URL stays `?document=101`) – anything else is dropped. @param {unknown} value */
const idParam = (value) => {
  const n = typeof value === 'number' ? value : typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : NaN;
  return Number.isInteger(n) && n > 0 ? n : undefined;
};
/** @param {unknown} value */
const pageParam = (value) => (Number.isInteger(Number(value)) && Number(value) > 1 ? Number(value) : undefined);

/**
 * Code-based route tree.
 * @param {{ queryClient: import('@tanstack/react-query').QueryClient, api: import('@blimo/api-client').BlimoApi, history?: import('@tanstack/react-router').RouterHistory }} options
 */
export function createAppRouter({ queryClient, api, history }) {
  const rootRoute = createRootRouteWithContext()({
    component: Outlet,
    notFoundComponent: NotFoundPage,
  });

  // --- public auth pages -------------------------------------------------
  const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    validateSearch: redirectSearch,
    beforeLoad: async ({ context, search }) => {
      const result = await loadMe(context).catch(() => null);
      if (result?.status === 'ok') throw redirect({ href: safeRedirect(search.redirect) });
    },
    component: LoginPage,
  });

  const twoFactorChallengeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/two-factor-challenge',
    validateSearch: redirectSearch,
    component: TwoFactorChallengePage,
  });

  const forgotPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/forgot-password',
    component: ForgotPasswordPage,
  });

  const resetPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reset-password/$token',
    validateSearch: (search) => ({ email: typeof search.email === 'string' ? search.email : undefined }),
    component: ResetPasswordPage,
  });

  // --- protected application ---------------------------------------------
  const appRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'app',
    beforeLoad: async ({ context, location }) => {
      const result = await loadMe(context);
      if (result.status === 'unauthenticated') {
        throw redirect({ to: '/login', search: { redirect: location.href } });
      }
      // 'two-factor-required' and missing app access are rendered by ProtectedLayout.
      return { auth: result };
    },
    component: ProtectedLayout,
  });

  const dashboardRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: OverviewPage });

  const profileRoute = createRoute({
    getParentRoute: () => appRoute,
    path: '/profil',
    component: ProfilePage,
  });

  // Visibility of properties/units is decided by the API (404 for foreign IDs).
  const propertiesRoute = createRoute({ getParentRoute: () => appRoute, path: '/immobilien', component: PropertiesPage });
  const propertyRoute = createRoute({
    getParentRoute: () => appRoute,
    path: '/immobilien/$propertyId',
    validateSearch: (search) => ({ tab: typeof search.tab === 'string' ? search.tab : undefined }),
    component: PropertyDetailPage,
  });
  const unitRoute = createRoute({ getParentRoute: () => appRoute, path: '/immobilien/einheiten/$unitId', component: UnitPage });
  // Finance (investor, finance.view): period/filter state lives in the URL.
  const developmentRoute = createRoute({ getParentRoute: () => appRoute, path: '/entwicklung', validateSearch: financeSearch, component: DevelopmentPage });
  const openItemsRoute = createRoute({ getParentRoute: () => appRoute, path: '/offene-posten', validateSearch: financeSearch, component: OpenItemsPage });
  const costsRoute = createRoute({ getParentRoute: () => appRoute, path: '/kosten', validateSearch: financeSearch, component: CostsPage });
  // Belegprüfung (auditors, owners): visibility of checks/folders/documents is decided by the API.
  const documentChecksRoute = createRoute({ getParentRoute: () => appRoute, path: '/belegpruefung', component: DocumentChecksPage });
  const documentCheckRoute = createRoute({
    getParentRoute: () => appRoute,
    path: '/belegpruefung/$checkId',
    validateSearch: (search) => ({
      tab: typeof search.tab === 'string' ? search.tab : undefined,
      document: idParam(search.document),
      compare: idParam(search.compare),
      page: pageParam(search.page),
    }),
    component: DocumentCheckPage,
  });
  const announcementsRoute = createRoute({
    getParentRoute: () => appRoute,
    path: '/mitteilungen',
    validateSearch: (search) => ({ page: pageParam(search.page) }),
    component: AnnouncementsPage,
  });
  const announcementRoute = createRoute({ getParentRoute: () => appRoute, path: '/mitteilungen/$announcementId', component: AnnouncementPage });
  const messagesRoute = createRoute({
    getParentRoute: () => appRoute,
    path: '/nachrichten',
    validateSearch: (search) => ({ page: pageParam(search.page) }),
    component: MessagesPage,
  });
  const newMessageRoute = createRoute({
    getParentRoute: () => appRoute,
    path: '/nachrichten/neu',
    validateSearch: (search) => ({ unit: idParam(search.unit) }),
    component: NewMessagePage,
  });
  const messageThreadRoute = createRoute({ getParentRoute: () => appRoute, path: '/nachrichten/$threadId', component: MessageThreadPage });
  // Zählerablesung (meter readers): only own meters (API); state of the filters in the URL.
  const meterReadingsRoute = createRoute({
    getParentRoute: () => appRoute,
    path: '/zaehlerablesung',
    validateSearch: (search) => ({
      q: typeof search.q === 'string' && search.q.trim() !== '' ? search.q : undefined,
      open: search.open === '1' || search.open === 1 || search.open === true ? 1 : undefined,
    }),
    component: MeterReadingsPage,
  });
  // Vollmachten (owners, ownerships.view)
  const proxiesRoute = createRoute({
    getParentRoute: () => appRoute,
    path: '/vollmachten',
    validateSearch: (search) => ({ all: search.all === '1' || search.all === 1 || search.all === true ? 1 : undefined }),
    component: ProxiesPage,
  });
  const proxyCreateRoute = createRoute({ getParentRoute: () => appRoute, path: '/vollmachten/neu', component: ProxyCreatePage });
  const proxyRoute = createRoute({ getParentRoute: () => appRoute, path: '/vollmachten/$proxyId', component: ProxyPage });
  const lettingsRoute = createRoute({
    getParentRoute: () => appRoute,
    path: '/vermietung',
    validateSearch: (search) => ({
      open: search.open === '1' || search.open === 1 || search.open === true ? 1 : undefined,
      vat: search.vat === 'gross' ? 'gross' : undefined, // G-21: Standard netto
    }),
    component: LettingsPage,
  });
  const serviceRoute = createRoute({ getParentRoute: () => appRoute, path: '/service', component: ServicePage });
  // Mieter (Bewohnerliste, tenancies.view): filters in the URL.
  const tenanciesRoute = createRoute({ getParentRoute: () => appRoute, path: '/mieter', validateSearch: tenancySearch, component: TenanciesPage });
  // Dokumente (documents.view): folders/documents decided by the API; `?document=` highlights one document.
  const documentsRoute = createRoute({
    getParentRoute: () => appRoute,
    path: '/dokumente',
    validateSearch: (search) => ({
      property: idParam(search.property),
      scope: search.scope === 'global' ? 'global' : undefined,
      document: idParam(search.document),
    }),
    component: DocumentsPage,
  });
  // Blimo AI (assistant.use): `?c=` = open conversation. Loaded lazily (markdown renderer).
  const assistantRoute = createRoute({
    getParentRoute: () => appRoute,
    path: '/assistent',
    validateSearch: (search) => ({
      c: idParam(search.c),
      q: typeof search.q === 'string' && search.q.trim() !== '' ? search.q.slice(0, 4000) : undefined,
    }),
    component: lazyRouteComponent(() => import('./routes/assistant/page.jsx'), 'AssistantPage'),
  });
  const featureRoutes = [
    documentsRoute,
    assistantRoute,
    meterReadingsRoute,
    proxiesRoute,
    proxyCreateRoute,
    proxyRoute,
    lettingsRoute,
    serviceRoute,
    tenanciesRoute,
    propertiesRoute,
    propertyRoute,
    unitRoute,
    developmentRoute,
    openItemsRoute,
    costsRoute,
    documentChecksRoute,
    documentCheckRoute,
    announcementsRoute,
    announcementRoute,
    messagesRoute,
    newMessageRoute,
    messageThreadRoute,
  ];
  const implemented = new Set(['/', '/profil', ...featureRoutes.map((r) => r.options.path)]);

  const placeholderRoutes = portalNav
    .filter((item) => !implemented.has(item.to))
    .map((item) =>
      createRoute({
        getParentRoute: () => appRoute,
        path: item.to,
        component: function NavPlaceholder() {
          return <PlaceholderPage item={item} />;
        },
      }),
    );

  const routeTree = rootRoute.addChildren([
    loginRoute,
    twoFactorChallengeRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    appRoute.addChildren([dashboardRoute, profileRoute, ...featureRoutes, ...placeholderRoutes]),
  ]);

  return createRouter({
    routeTree,
    history,
    context: { queryClient, api },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    // native-feeling push/pop/tab transitions where supported (lib/transitions.js, styles.css)
    defaultViewTransition: { types: transitionTypes },
  });
}
